/**
 * „Nowy program" / „Nowy plan" — one window, two steps: where the plan starts, then its details.
 *
 * Step 1 („Program") is canvas rows H and I, owner-approved on 23 September 2026: start from
 * nothing, or copy a library template, with the choice previewed beside it (`ProgramStep`). Step 2
 * („Szczegóły") is variant D of `docs/design/plan-propozycje-2026-09-21/` (`#d/create/*`), chosen
 * on 21 September: the name on the left and the plan SYSTEM on the right, alive. A copy's details
 * are prefilled from its template and its system is drawn LOCKED — it inherits the template's.
 *
 * ── Two entries, one dialog ──────────────────────────────────────────────────────────────────
 * The plan library opens it with no `recipient` and the plan becomes a library entry on its first
 * Zapisz. The client detail opens it FOR a client: the title says „Nowy plan", the head echoes the
 * client, and the builder it opens assigns the plan to them instead of saving it.
 *
 * ── The window never changes size between the steps ──────────────────────────────────────────
 * Both steps stand in `shared/ui/splitDialog`'s two panes. What variant D withdrew was a window
 * that changed WIDTH between steps; a dialog that resizes moves its primary under the trainer's
 * pointer.
 *
 * ── The system is the only irreversible thing here, and it is the thing that MOVES ──────────
 * `self-paced-plan-requirements.md` R1 fixes a plan's scheduling mode at creation, and neither this
 * app nor the phone offers anything that changes it afterwards — the backend refuses a change to a
 * plan somebody is on with `plan_mode_locked`. So the right half is not decoration: the illustration
 * that plays there is the argument, in the beat before the decision, and it is the phone's own
 * (`shared/ui/planSystemViz`, the same 5400ms loop and the same stops).
 *
 * That is a statement about the CLIENTS, not about the backend. An unassigned library template can
 * be re-based by a full resubmit and the backend delivers it; building that surface is out of
 * scope, and claiming it is impossible is not the same as not having built it.
 *
 * ── `Dialog`, not `AlertDialog` ──────────────────────────────────────────────────────────────
 * Nothing is written until the builder's first Zapisz, so an abandoned plan costs nothing and both
 * Escape and the backdrop dismiss. `finalFocus` is not optional: this dialog has no
 * `Dialog.Trigger` — the button belongs to the caller's own bar — so floating-ui has no reference
 * to restore to and focus would land on `<body>`.
 *
 * ── A step change moves focus to where the step starts ───────────────────────────────────────
 * „Dalej" puts the caret in the name field and „Wstecz" puts it back on the chosen start. Both are
 * `flushSync` then `focus()`, in the handler: the control that had focus is unmounted by the
 * step change, and an effect would leave focus on `<body>` for a frame, which a screen reader
 * announces as the whole document.
 *
 * ── A refused „Utwórz i otwórz kreator" explains itself three ways ──────────────────────────
 * The sentence is drawn in the footer, the button repeats it on `title`, and pressing the refused
 * button MOVES FOCUS to the name field and selects it. It used to carry the reason in a clipped
 * span alone, so the only trainer ever told the plan needed a name was one using a screen reader.
 */

import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import { flushSync } from 'react-dom';
import { Dialog } from '@base-ui/react/dialog';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import {
  PLAN_CATEGORY_ORDER,
  TEXT_LIMITS,
  discardLeftoverPlan,
  readNewPlanEntry,
  resumeLeftoverPlan,
  sweepUnsavedPlanRecord,
  type NewPlanEntry,
  type PlanRecipient,
  type PlanSchedulingMode,
} from '~/entities/plan-draft';
import { useSessionUser } from '~/entities/session';
import { useScreenMotionPreference } from '~/shared/design-system/motion';
import { LockIcon } from '~/shared/ui/AuthIcons';
import { CheckGlyph, CloseGlyph } from '~/shared/ui/FormGlyphs';
import { CalendarWeekViz, SelfPacedSequenceViz } from '~/shared/ui/planSystemViz';
import { planCategoryColour } from '~/shared/ui/planCategoryColour';
import ember from '~/shared/ui/emberButton.module.css';
import segmented from '~/shared/ui/segmented.module.css';
import split from '~/shared/ui/splitDialog.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import hiddenControl from '~/shared/ui/visuallyHiddenControl.module.css';

import { useCreatePlanFlow, type CreatePlanFlow } from '../lib/useCreatePlanFlow';
import type { CreatePlanStep } from '../model/createPlanFlow';

import { CoverPicker } from './CoverPicker';
import { ProgramChoice, ProgramPreviewPane } from './ProgramStep';
import { ResumePlanDialog } from './ResumePlanDialog';
import styles from './CreatePlanDialog.module.css';

/**
 * Wire mode → the key its copy lives under. Explicit, because the two vocabularies differ: the
 * draft calls them `date` / `selfpaced` and the locale bundle calls them `dateBased` / `selfPaced`,
 * after the backend's own `SchedulingMode` members.
 */
const SYSTEM_COPY: Readonly<Record<PlanSchedulingMode, 'dateBased' | 'selfPaced'>> = {
  date: 'dateBased',
  selfpaced: 'selfPaced',
};

export interface CreatePlanDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /**
   * Where focus goes when this closes — the control that opened it.
   *
   * A REF where the opener is always there, and a CALLBACK where it might not be: Base UI resolves
   * the callback at close time, which is the one moment that can answer "and what is still on
   * screen".
   */
  readonly finalFocus: RefObject<HTMLElement | null> | (() => HTMLElement | null);
  /** The client the plan is built for, from the client detail. Absent from the plan library. */
  readonly recipient?: PlanRecipient;
}

/**
 * „+ Nowy program" and a client's „Przypisz plan" — every entry that starts a NEW unsaved plan — as
 * one act, whatever it turns out to need.
 *
 * With unsaved work still around — a new plan in this tab, or one this device kept after a reload —
 * it opens `ResumePlanDialog` first (owner, 23 September 2026), and the create window only after
 * „Zacznij od nowa". With none it opens the create window directly, exactly as before. The caller
 * passes the same props either way and never learns which it was. The leftover plan is offered
 * whoever it was being built for: it is the one unsaved plan this device holds, and starting another
 * over it without asking is the loss the prompt exists to prevent.
 *
 * The question is asked ONCE per opening, and its answer is held for as long as the dialog stays
 * open — so the resume card keeps its content through its own exit, and the create window does not
 * flip back to a resume prompt when the record it just discarded is gone. It is asked during render
 * because it is a synchronous read of this tab's store and `localStorage`, and deciding it before
 * the first paint is what keeps the wrong dialog from drawing for a frame.
 *
 * It is asked only once the signed-in user is KNOWN. A degraded boot has a session whose user has
 * not been confirmed, and answering for nobody would open the create window over a plan the device
 * is keeping — and seed a new plan whose writes replace it. Until the user is known, neither window
 * opens; the press is held and answered when the session is.
 */
type Stage = 'resume' | 'handover' | 'create';

export function CreatePlanDialog({ open, onClose, finalFocus, recipient }: CreatePlanDialogProps) {
  const userId = useSessionUser()?.id ?? null;
  const navigate = useNavigate();
  const [opening, setOpening] = useState<{
    readonly entry: NewPlanEntry;
    readonly stage: Stage;
  } | null>(null);
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setOpening(null);
  }
  if (open && opening === null && userId !== null) {
    const entry = readNewPlanEntry(userId);
    setOpening({ entry, stage: entry.kind === 'resume' ? 'resume' : 'create' });
  }

  // A broken record is removed from here rather than during the render that judged it.
  useEffect(() => {
    if (open && userId !== null) sweepUnsavedPlanRecord(userId);
  }, [open, userId]);

  const leftover = opening?.entry.kind === 'resume' ? opening.entry.plan : null;
  const stage = opening?.stage ?? null;
  // The create window opened by a hand-over — its scrim is already on screen, held by the prompt.
  const arrivedFromPrompt = leftover !== null;

  return (
    <>
      <ResumePlanDialog
        open={open && stage === 'resume'}
        plan={leftover}
        handingOver={stage === 'handover'}
        finalFocus={finalFocus}
        onDismiss={onClose}
        onContinue={() => {
          if (leftover !== null && userId !== null) resumeLeftoverPlan(leftover, userId);
          onClose();
          void navigate({ to: '/plans/nowy' });
        }}
        onStartFresh={() => {
          if (leftover !== null && userId !== null) discardLeftoverPlan(leftover, userId);
          setOpening((current) => (current === null ? null : { ...current, stage: 'handover' }));
        }}
        onClosed={() => {
          setOpening((current) =>
            current?.stage === 'handover' ? { ...current, stage: 'create' } : current,
          );
        }}
      />
      <Dialog.Root
        open={open && stage === 'create'}
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
      >
        <Dialog.Portal>
          <Dialog.Backdrop
            className={
              arrivedFromPrompt
                ? `${String(split.backdrop)} ${String(styles.scrimArrives)}`
                : split.backdrop
            }
          />
          <CreatePlanPopup
            onClose={onClose}
            finalFocus={finalFocus}
            recipient={recipient ?? null}
            risesIn={arrivedFromPrompt}
          />
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

function CreatePlanPopup({
  onClose,
  finalFocus,
  recipient,
  risesIn,
}: {
  readonly onClose: () => void;
  readonly finalFocus: CreatePlanDialogProps['finalFocus'];
  readonly recipient: PlanRecipient | null;
  /** Arriving from the leftover-plan prompt: the window rises as well as fades. */
  readonly risesIn: boolean;
}) {
  const { t } = useTranslation();
  const reducedMotion = useScreenMotionPreference();
  const flow = useCreatePlanFlow(onClose, recipient);
  const heading = useRef<HTMLHeadingElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const refusalId = useId();
  /**
   * The name field — the one thing that unblocks the primary.
   *
   * It lives up here because two components need it: the field attaches it, and the refused submit
   * hands focus to it. A ref passed DOWN, never one read during render.
   */
  const nameInput = useRef<HTMLInputElement>(null);
  const onProgram = flow.step === 'program';
  const refusal = onProgram ? flow.programRefusal : flow.refusal;

  const toDetails = () => {
    // A refused `next` is a no-op, and the name field it would focus is not mounted on this step.
    flushSync(() => {
      flow.next();
    });
    nameInput.current?.focus();
  };
  const toProgram = () => {
    flushSync(() => {
      flow.back();
    });
    // The chosen start's own radio — the one Tab stop the group has. The scratch radio, first in the
    // group, when the list is not drawing the chosen row (its read is still open or failed): focus
    // must land INSIDE the step, never stay on the foot, where the same place now holds Anuluj.
    const starts = popup.current?.querySelectorAll<HTMLInputElement>('input[data-start-choice]');
    const target = Array.from(starts ?? []).find((radio) => radio.checked) ?? starts?.[0];
    target?.focus();
  };

  return (
    <Dialog.Popup
      ref={popup}
      className={risesIn ? `${String(split.popup)} ${String(styles.risesIn)}` : split.popup}
      initialFocus={heading}
      finalFocus={finalFocus}
    >
      <div className={split.head}>
        <Dialog.Title
          render={<h2 ref={heading} className={split.title} tabIndex={-1} />}
        >
          {recipient === null ? t('web:planCreate.title') : t('web:planCreate.titleForClient')}
        </Dialog.Title>
        {/* No inflection: „dla: Maja Lewandowska", the nominative the roster has, because a Polish
          * surname's genitive is not derivable and this app never guesses one. */}
        {recipient !== null && (
          <span className={split.echo}>{t('web:planCreate.echo', { name: recipient.clientName })}</span>
        )}
        <span className={split.spacer} />
        <Steps step={flow.step} />
        <span className={split.spacer} />
        {/* The cross the specimen's head draws, and the one "Przypisz plan" already has. Anuluj in
          * the foot says the same thing, and that is fine: the two are the head's affordance and
          * the foot's, and a window with a dismiss in only one of them is the odd one out. */}
        <Dialog.Close className={split.close} aria-label={t('web:planCreate.close')}>
          <CloseGlyph />
        </Dialog.Close>
      </div>

      <div className={split.form}>
        {onProgram ? <ProgramChoice flow={flow} /> : <DetailsForm flow={flow} nameRef={nameInput} />}
      </div>
      <div className={split.live}>
        {onProgram ? (
          <ProgramPreviewPane flow={flow} />
        ) : (
          <SystemChoice flow={flow} reducedMotion={reducedMotion} />
        )}
      </div>

      <div className={split.foot}>
        {/* Keyed apart: without the keys React reuses ONE button for both arms, so the Wstecz that
          * had focus became Anuluj under the caret and a second Enter discarded the whole form. */}
        {onProgram ? (
          <button key="cancel" type="button" className={split.ghost} onClick={onClose}>
            {t('web:planCreate.cancel')}
          </button>
        ) : (
          <button key="back" type="button" className={split.ghost} onClick={toProgram}>
            {t('web:planCreate.back')}
          </button>
        )}
        <span className={split.spacer} />
        {/* The chosen start in one line (canvas H1/H2/I1). Only while nothing is refused: the
          * refusal is the sentence the trainer needs, and the primary never moves for either. */}
        {onProgram && refusal === null && flow.startSummary !== null && (
          <span className={styles.startSummary}>{flow.startSummary}</span>
        )}
        {/* The refusal beside the primary rather than only behind it. Always in the tree, so the
          * id the button names never points at nothing. */}
        <span id={refusalId} className={split.reason}>
          {refusal ?? ''}
        </span>
        {onProgram ? (
          <button
            type="button"
            className={`${ember.ember} ${split.primary}`}
            aria-disabled={refusal === null ? undefined : 'true'}
            aria-describedby={refusal === null ? undefined : refusalId}
            title={refusal ?? undefined}
            onClick={toDetails}
          >
            {t('web:planCreate.next')}
          </button>
        ) : (
          <SubmitButton flow={flow} reasonId={refusalId} nameRef={nameInput} />
        )}
      </div>
    </Dialog.Popup>
  );
}

/**
 * „Program · Szczegóły" — where the trainer is in the two steps. Read-only: „Wstecz" is the way
 * back, and a step that could be pressed would skip the one gate between them. The current step is
 * `aria-current`, and a finished one says so in words as well as with its check.
 */
function Steps({ step }: { readonly step: CreatePlanStep }) {
  const { t } = useTranslation();
  const steps: readonly CreatePlanStep[] = ['program', 'details'];
  const current = steps.indexOf(step);

  return (
    <ol className={styles.steps} aria-label={t('web:planCreate.steps.label')}>
      {steps.map((entry, index) => {
        const done = index < current;
        const on = index === current;
        return (
          <li
            key={entry}
            className={`${String(styles.step)} ${on ? String(styles.stepOn) : ''}`}
            aria-current={on ? 'step' : undefined}
          >
            <span className={`${String(styles.stepIndex)} ${on ? String(styles.stepIndexOn) : ''}`} aria-hidden="true">
              {done ? <CheckGlyph /> : index + 1}
            </span>
            {t(`web:planCreate.steps.${entry}`)}
            {done && <span className={hidden.srOnly}>{`, ${t('web:planCreate.steps.done')}`}</span>}
          </li>
        );
      })}
    </ol>
  );
}

/** The left half: what the program is called, what kind it is, and what it looks like. */
function DetailsForm({
  flow,
  nameRef,
}: {
  readonly flow: CreatePlanFlow;
  readonly nameRef: RefObject<HTMLInputElement | null>;
}) {
  const { t } = useTranslation();
  const nameId = useId();
  const descriptionId = useId();
  const categoryName = useId();

  return (
    <>
      <h3 className={styles.question}>{t('web:planCreate.details.question')}</h3>

      <div className={styles.field}>
        <span className={styles.label}>
          <label htmlFor={nameId}>{t('web:planCreate.details.nameLabel')}</label>
          <span className={styles.required}>{t('web:planCreate.details.required')}</span>
          <span className={styles.spacer} />
          <span className={styles.counter}>{flow.nameCount}</span>
        </span>
        {/* `maxLength` is the wire's own ceiling, enforced where the trainer can see the answer.
          * Without it a pasted 250 characters was accepted here, carried into the builder,
          * survived a whole plan being authored, and came back as a 400 on the first Zapisz —
          * with no draft persistence, so the work was gone. */}
        <input
          id={nameId}
          ref={nameRef}
          className={styles.input}
          type="text"
          value={flow.choices.name}
          maxLength={TEXT_LIMITS.planName.max}
          autoComplete="off"
          placeholder={t('web:planCreate.details.namePlaceholder')}
          onChange={(event) => {
            flow.setName(event.target.value);
          }}
        />
      </div>

      <fieldset className={styles.field}>
        <legend className={styles.label}>
          {t('web:planCreate.details.categoryLabel')}
          <span className={styles.required}>{t('web:planCreate.details.required')}</span>
        </legend>
        <div className={styles.categories}>
          {PLAN_CATEGORY_ORDER.map((category) => {
            const selected = flow.choices.category === category;
            return (
              <label
                key={category}
                className={`${styles.categoryTile} ${selected ? styles.categoryTileOn : ''}`}
                style={planCategoryColour(category)}
              >
                <input
                  className={hiddenControl.control}
                  type="radio"
                  name={categoryName}
                  value={category}
                  checked={selected}
                  onChange={() => {
                    flow.setCategory(category);
                  }}
                />
                <span className={styles.categoryDot} aria-hidden="true" />
                <span className={styles.categoryName}>{t(`plans.category.${category}`)}</span>
              </label>
            );
          })}
        </div>
      </fieldset>

      <div className={styles.field}>
        <span className={styles.label}>
          <label htmlFor={descriptionId}>{t('web:planCreate.details.descriptionLabel')}</label>
          <span className={styles.required}>{t('web:planCreate.details.optional')}</span>
        </span>
        <textarea
          id={descriptionId}
          className={styles.textarea}
          value={flow.choices.description}
          maxLength={TEXT_LIMITS.planDescription.max}
          placeholder={t('web:planCreate.details.descriptionPlaceholder')}
          onChange={(event) => {
            flow.setDescription(event.target.value);
          }}
        />
      </div>

      <CoverPicker flow={flow} />

      {/* What a copy does to its template, said where the copy is named. */}
      {flow.copyOf !== null && (
        <p className={styles.note}>
          {flow.recipient === null
            ? t('web:planCreate.copy.libraryNote', { template: flow.copyOf })
            : t('web:planCreate.copy.clientNote', { template: flow.copyOf })}
        </p>
      )}
    </>
  );
}

/**
 * The right half: the segmented control — or, on a copy, the locked answer — the illustration it
 * names, and the lock note.
 *
 * ── The two vizes share a height, so only the INK changes ───────────────────────────────────
 * Switching the segment swaps one drawing for the other inside a box that does not move. A panel
 * that resized on the switch would make the two impossible to compare, which is the whole thing
 * this half is for.
 */
function SystemChoice({
  flow,
  reducedMotion,
}: {
  readonly flow: CreatePlanFlow;
  readonly reducedMotion: boolean;
}) {
  const { t } = useTranslation();
  const options: readonly PlanSchedulingMode[] = ['date', 'selfpaced'];
  const chosen = SYSTEM_COPY[flow.choices.mode];

  return (
    <>
      {flow.copyOf === null ? (
        <div className={styles.systemHead}>
          <b className={styles.systemTitle}>{t('web:planCreate.system.question')}</b>
          <span className={styles.systemNote}>{t('web:planCreate.system.permanent')}</span>
          <span className={split.spacer} />
          <div className={segmented.seg} role="radiogroup" aria-label={t('web:planCreate.system.question')}>
            {options.map((mode) => {
              const selected = flow.choices.mode === mode;
              return (
                <label
                  key={mode}
                  className={`${String(segmented.segOption)} ${selected ? String(segmented.segOptionOn) : ''}`}
                >
                  <input
                    className={hiddenControl.control}
                    type="radio"
                    name="plan-system"
                    value={mode}
                    checked={selected}
                    onChange={() => {
                      flow.setMode(mode);
                    }}
                  />
                  {t(`web:planCreate.system.${SYSTEM_COPY[mode]}.title`)}
                </label>
              );
            })}
          </div>
        </div>
      ) : (
        // A copy's system is its template's (R1), so there is no control at all — a segment drawn
        // disabled would still say there is a choice. The lock is the statement, and the sentence
        // under the answer says where it came from.
        <div className={styles.systemLocked}>
          <b className={styles.systemTitle}>{t('web:planCreate.system.locked.title')}</b>
          <div className={styles.lockedRow}>
            <LockIcon className={styles.lockGlyph} />
            <span className={styles.lockedText}>
              <span className={styles.lockedValue}>
                {t(`web:planCreate.system.${chosen}.title`)}
              </span>
              <span className={styles.systemNote}>
                {t('web:planCreate.system.locked.note', { template: flow.copyOf })}
              </span>
            </span>
          </div>
        </div>
      )}

      <div className={styles.systemViz}>
        <div className={styles.systemDrawing}>
          {/* `running` is always true here: the panel is the reason the dialog is this shape, and
            * the loop stops when the popup unmounts. `reducedMotion` parks it on its last frame. */}
          {flow.choices.mode === 'date' ? (
            <CalendarWeekViz reducedMotion={reducedMotion} running />
          ) : (
            <SelfPacedSequenceViz reducedMotion={reducedMotion} running />
          )}
        </div>
        <p className={styles.systemBody}>
          <b>{t(`web:planCreate.system.${chosen}.lede`)}</b> {t(`web:planCreate.system.${chosen}.body`)}
        </p>
      </div>

      {/* The one warning, and the only place in the app this fork is described as permanent. A
        * sentence rather than a badge: the cost lands after the trainer leaves this dialog, so the
        * only useful form is the words. A copy has no fork to warn about — its row above says so. */}
      {flow.copyOf === null && <p className={styles.lock}>{t('web:planCreate.system.lockNote')}</p>}
    </>
  );
}

/** Create, or say why not — visibly, and by putting the trainer where the answer is. */
function SubmitButton({
  flow,
  reasonId,
  nameRef,
}: {
  readonly flow: CreatePlanFlow;
  readonly reasonId: string;
  readonly nameRef: RefObject<HTMLInputElement | null>;
}) {
  const { t } = useTranslation();
  const blocked = flow.refusal !== null;

  return (
    <button
      type="button"
      className={`${ember.ember} ${split.primary}`}
      // `aria-disabled`, never `disabled`: a plain one drops the control out of the tab order and
      // takes the explanation with it (view ADR §12).
      aria-disabled={blocked ? 'true' : undefined}
      aria-describedby={blocked ? reasonId : undefined}
      title={flow.refusal ?? undefined}
      onClick={() => {
        if (!blocked) {
          flow.create();
          return;
        }
        // `select()` as well as focus: the field may already hold whitespace the trainer typed, and
        // a caret parked after three spaces looks like a field that is already filled in.
        nameRef.current?.focus();
        nameRef.current?.select();
      }}
    >
      {flow.recipient === null ? t('web:planCreate.submit') : t('web:planCreate.submitForClient')}
    </button>
  );
}
